import React, { useState, useRef } from "react";
import { Plus, ChevronDown, Trash2, UserX, UserCheck } from "lucide-react";
import { toast } from "sonner";
import PlanUsage, { PLAN_FULL_TITLE, usePlanUsage } from "../../components/PlanUsage";
import { useSelector } from "react-redux";
import FleetrifyCard from "../../components/Card";
import DashboardLayout from "../../components/DashboardLayout";
import { DashboardFleetSummaryCard } from "../../components/pages/Dashboard/Cards/summary";
import StaffTable, { Staff as StaffType } from "../../components/Table/StaffTable";
import StaffUpload from "../../components/pages/Users/modal/StaffManualUpload";
import StaffBulkUpload from "../../components/pages/Users/modal/StaffBulkUpload";
import StaffProfile from "../../components/pages/Users/modal/StaffProfile";
import { useStaffController } from "../../controllers/useStaffController";
import {
    useDeleteInviteMutation,
    useDeactivateStaffMutation,
    useReactivateStaffMutation,
    useDeleteStaffMutation,
    useGetStaffByCompanyQuery,
} from "../../redux/services/staffApi";
import { FleetrifyModal } from "../../components/Modal/modal";
import { usePermission } from "../../utils/permissions";
import { usePermission as useHookPermission } from "../../hooks/usePermission";
import { selectIsAdmin } from "../../redux/slices/authSlice";
import { useSingleUserController } from "../../controllers/useSingleUserController";
import StaffOverview from "../../components/pages/Users/modal/StaffOverview";

type ModalAction = "updatestaff" | "update" | "resend" | "add" | null;
type ConfirmType = "delete" | "deactivate" | "reactivate" | null;

const Staff = () => {
    const { can } = usePermission();
    const isAdmin = useSelector(selectIsAdmin);
    const { userDetails } = useSingleUserController();
    const getRoleName = (roleData: any): string => {
        if (typeof roleData === "object" && roleData?.name) return roleData.name;
        return typeof roleData === "string" ? roleData : "Role";
    };
    const role = getRoleName(userDetails?.role);

    const {
        view, setView, showBulkUploadModal, setShowBulkUploadModal,
        totalStaff, activeStaff, inactiveStaff,
        totalInvites, pendingInvites, rejectedInvites,
        staffData, refetch, staffPage, setStaffPage, staffLastPage, staffTotal,
        invitationTableData, invitePage, setInvitePage, inviteLastPage, inviteTotal, pageSize,
    } = useStaffController();
    console.log("Invitation Data: ", invitationTableData);

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    const [selectedStaff, setSelectedStaff] = useState<StaffType | null>(null);
    const [modalAction, setModalAction] = useState<ModalAction>(null);

    // ── Confirm modal state ───────────────────────────────
    const [confirmType, setConfirmType] = useState<ConfirmType>(null);
    const [staffToAct, setStaffToAct] = useState<StaffType | null>(null);
    const [isConfirming, setIsConfirming] = useState(false);

    const [deleteInvite, { isLoading: isDeletingInvite }] = useDeleteInviteMutation();
    const [deactivateStaff] = useDeactivateStaffMutation();
    const [reactivateStaff] = useReactivateStaffMutation();
    const [deleteStaff, { isLoading: isDeletingStaff }] = useDeleteStaffMutation();
    const { data: staffByCompany, refetch: refetchCompany, isLoading: isLoadingCompanyStaff } = useGetStaffByCompanyQuery();
    const { hasPermission } = useHookPermission();

    const extractStaffList = (raw: any): any[] => {
        if (!raw) return [];
        return raw.data?.staff_members || raw.staff_members || [];
    };

    const closeConfirmModal = () => {
        setConfirmType(null);
        setStaffToAct(null);
        setIsConfirming(false);
    };

    // ── Confirm action handler ────────────────────────────
    const handleConfirmAction = async () => {
        if (!staffToAct || !confirmType) return;
        setIsConfirming(true);

        try {
            if (confirmType === "delete") {
                if (!hasPermission("delete staff")) {
                    toast.error("You don't have permission to delete staff");
                    return;
                }

                if (modalAction === "updatestaff" || view === "overview") {
                    // Deleting actual staff
                    let staffList = extractStaffList(staffByCompany);
                    if (!staffList.length) {
                        const refreshed = await refetchCompany();
                        staffList = extractStaffList(refreshed?.data);
                    }
                    const matchedStaff = staffList.find((s: any) => {
                        const idMatch = s.id === staffToAct.id || s.user_id === staffToAct.id || s.user?.id === staffToAct.id;
                        const emailMatch = staffToAct?.email && s.user?.email === staffToAct.email;
                        return idMatch || emailMatch;
                    });
                    if (!matchedStaff?.id) {
                        toast.error("Could not find staff record to delete. Try refreshing.");
                        return;
                    }
                    await deleteStaff({ staffId: matchedStaff.id }).unwrap();
                } else {
                    // Deleting an invitation
                    await deleteInvite({ id: staffToAct.id }).unwrap();
                }
                toast.success("Deleted successfully");

            } else if (confirmType === "deactivate") {
                if (!hasPermission("deactivate staff")) {
                    toast.error("You don't have permission to deactivate staff");
                    return;
                }
                await deactivateStaff({ staffId: staffToAct.id, reason: "No longer active" }).unwrap();
                toast.success("Staff deactivated successfully");

            } else if (confirmType === "reactivate") {
                if (!hasPermission("activate staff")) {
                    toast.error("You don't have permission to reactivate staff");
                    return;
                }
                await reactivateStaff({ staffId: staffToAct.id }).unwrap();
                toast.success("Staff reactivated successfully");
            }

            closeConfirmModal();
            refetch();
            refetchCompany();
        } catch (err: any) {
            toast.error(err?.data?.message || err?.error || "Action failed. Please try again.");
        } finally {
            setIsConfirming(false);
        }
    };

    const isModalAction = (action: string): action is Exclude<ModalAction, null> =>
        ["updatestaff", "update", "resend", "add"].includes(action);

    const handleActionClick = async (
        action: "delete" | "update" | "updatestaff" | "deactivate" | "reactivate" | "resend" | "add",
        staff?: StaffType
    ) => {
        const actionPermissions: Record<string, string> = {
            delete: "delete staff",
            updatestaff: "update staff",
            deactivate: "deactivate staff",
            reactivate: "activate staff",
            update: "update staff",
            resend: "resend invitation to staff",
            add: "add staff",
        };

        if ((!isAdmin || role !== "Super Admin") && !can(actionPermissions[action])) {
            toast.error("You don't have permission to perform this action.");
            return;
        }
        if (!hasPermission(actionPermissions[action])) {
            toast.error("You don't have permission to perform this action");
            return;
        }

        if (isModalAction(action)) {
            setModalAction(action);
            setSelectedStaff(action === "add" ? null : staff || null);
            setView("manualUpload");
        }

        if (action === "delete" && staff) {
            setStaffToAct(staff);
            setConfirmType("delete");
            return;
        }

        if (action === "deactivate" && staff) {
            setStaffToAct(staff);
            setConfirmType("deactivate");
            return;
        }

        if (action === "reactivate" && staff) {
            setStaffToAct(staff);
            setConfirmType("reactivate");
            return;
        }
    };

    // ── Modal config per confirmType ──────────────────────
    const confirmConfig = {
        delete: {
            icon: <Trash2 size={22} className="text-red-500" />,
            iconBg: "bg-red-50 border-red-200",
            title: "Delete Staff",
            body: (name: string) => (
                <>
                    You are about to delete{" "}
                    <span className="font-semibold text-[#00032A]">{name}</span>.
                    This action <span className="text-red-600 font-medium">cannot be undone</span>.
                </>
            ),
            confirmLabel: "Yes, Delete",
            confirmClass: "bg-red-600 hover:bg-red-700 text-white",
            loadingLabel: "Deleting...",
        },
        deactivate: {
            icon: <UserX size={22} className="text-orange-500" />,
            iconBg: "bg-orange-50 border-orange-200",
            title: "Deactivate Staff",
            body: (name: string) => (
                <>
                    Are you sure you want to deactivate{" "}
                    <span className="font-semibold text-[#00032A]">{name}</span>?
                    They will lose access until reactivated.
                </>
            ),
            confirmLabel: "Yes, Deactivate",
            confirmClass: "bg-orange-500 hover:bg-orange-600 text-white",
            loadingLabel: "Deactivating...",
        },
        reactivate: {
            icon: <UserCheck size={22} className="text-green-600" />,
            iconBg: "bg-green-50 border-green-200",
            title: "Reactivate Staff",
            body: (name: string) => (
                <>
                    Are you sure you want to reactivate{" "}
                    <span className="font-semibold text-[#00032A]">{name}</span>?
                    They will regain access immediately.
                </>
            ),
            confirmLabel: "Yes, Reactivate",
            confirmClass: "bg-green-600 hover:bg-green-700 text-white",
            loadingLabel: "Reactivating...",
        },
    };

    // Pending invites count as users toward the plan limit.
    const { isFull: usersFull } = usePlanUsage("users");

    const activeConfig = confirmType ? confirmConfig[confirmType] : null;
    const actingName = staffToAct?.name || staffToAct?.email || "this staff member";

    return (
        <DashboardLayout
            title="Staff"
            description="Manage your staff records, roles and branches. Upload manually or in bulk."
            containerClass="pb-10"
        >
            {/* Add Staff Button */}
            {view === "invitation" && (role === "Super Admin" || can("create staff")) && (
                <div className="flex flex-wrap justify-end items-center gap-3 mt-8">
                    <PlanUsage resource="users" />
                    <div className="relative inline-block text-left" ref={dropdownRef}>
                        <button
                            disabled={usersFull}
                            title={usersFull ? PLAN_FULL_TITLE : undefined}
                            onClick={() => {
                                if (!hasPermission("add staff")) {
                                    toast.error("You don't have permission to create staff");
                                    return;
                                }
                                setDropdownOpen((prev) => !prev);
                            }}
                            className="rounded-xl bg-[#143887] text-white px-4 py-2 text-sm font-medium flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Plus className="h-4 w-4" /> Add New Staff
                            <ChevronDown className="h-4 w-4" />
                        </button>
                        {dropdownOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-50">
                                <button
                                    // onClick={() => { setDropdownOpen(false); handleActionClick("add"); setView("manualUpload"); }}
                                    onClick={() => {
                                        setDropdownOpen(false);
                                        handleActionClick("add");
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                >
                                    Manual Upload
                                </button>
                                <button
                                    onClick={() => setShowBulkUploadModal(true)}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                >
                                    Bulk Upload
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-6 mt-10 border-b border-gray-200">
                <button onClick={() => setView("overview")}
                    className={`pb-2 text-sm font-semibold ${view === "overview" ? "text-[#143887] border-b-2 border-[#143887]" : "text-gray-500"}`}>
                    Staffs
                </button>
                <button onClick={() => setView("invitation")}
                    className={`pb-2 text-sm font-semibold ${view === "invitation" ? "text-[#143887] border-b-2 border-[#143887]" : "text-gray-500"}`}>
                    Invitations
                </button>
            </div>

            <main className="mt-10 space-y-10">
                {/* Staff Overview */}
                {view === "overview" && (
                    <>
                        <section className="bg-[#E7E9EE] p-4 rounded-lg mt-10">
                            <h3 className="text-sm font-semibold text-[#00032A] mb-4">Staff Overview</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <DashboardFleetSummaryCard title="Total Staff" value={totalStaff} changeText="Fleetrify Data" changeDirection="up" />
                                <DashboardFleetSummaryCard title="Active Staff" value={activeStaff} changeText="Fleetrify Data" changeDirection="up" />
                                <DashboardFleetSummaryCard title="Inactive Staff" value={inactiveStaff} changeText="Fleetrify Data" changeDirection="down" />
                            </div>
                        </section>

                        <FleetrifyCard cardContainerClass="mt-20">
                            {isLoadingCompanyStaff ? (
                                <div className="flex flex-col items-center justify-center py-20 text-gray-600">
                                    <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3" />
                                    <p className="text-sm">Loading Staffs Data...</p>
                                </div>
                            ) : staffData?.length ? (
                                <StaffTable
                                    tableData={staffData}
                                    pageSize={pageSize}
                                    totalPage={staffLastPage}
                                    total={staffTotal}
                                    currentPage={staffPage}
                                    setCurrentPage={setStaffPage}
                                    onRowClick={(row) => { setSelectedStaff(row); setModalAction(null); setView("profile"); }}
                                    onActionClick={handleActionClick}
                                    view="overview"
                                />
                            ) : (
                                <div className="flex flex-col items-center justify-center py-16 px-4">
                                    <div className="w-16 h-16 bg-[#E7E9EE] rounded-full grid place-items-center mb-4">
                                        <svg className="w-8 h-8 text-[#A1AFCF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-base font-semibold text-[#00032A] mb-1">No staff found</h3>
                                    <p className="text-sm text-gray-500 text-center max-w-xs">No staff members have been added yet.</p>
                                </div>
                            )}
                        </FleetrifyCard>
                    </>
                )}

                {/* Invitations */}
                {view === "invitation" && (
                    <>
                        <section className="bg-[#E7E9EE] p-4 rounded-lg mt-10">
                            <h3 className="text-sm font-semibold text-[#00032A] mb-4">Invitation Overview</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <DashboardFleetSummaryCard title="Total Invites" value={totalInvites} changeText="Fleetrify Data" changeDirection="up" />
                                <DashboardFleetSummaryCard title="Pending Invites" value={pendingInvites} changeText="Fleetrify Data" changeDirection="up" />
                                <DashboardFleetSummaryCard title="Declined Invites" value={rejectedInvites} changeText="Fleetrify Data" changeDirection="down" />
                            </div>
                        </section>

                        <FleetrifyCard cardContainerClass="mt-20">
                            {isLoadingCompanyStaff ? (
                                <div className="flex flex-col items-center justify-center py-20 text-gray-600">
                                    <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3" />
                                    <p className="text-sm">Loading Invitation Data...</p>
                                </div>
                            ) : invitationTableData?.length ? (
                                <StaffTable
                                    tableData={invitationTableData}
                                    pageSize={pageSize}
                                    totalPage={inviteLastPage}
                                    total={inviteTotal}
                                    currentPage={invitePage}
                                    setCurrentPage={setInvitePage}
                                    onRowClick={(row) => {
                                        if (view !== "invitation") { setSelectedStaff(row); setModalAction(null); setView("profile"); }
                                    }}
                                    onActionClick={handleActionClick}
                                    showActions
                                    view="invitation"
                                />
                            ) : (
                                <div className="flex flex-col items-center justify-center py-16 px-4">
                                    <div className="w-16 h-16 bg-[#E7E9EE] rounded-full grid place-items-center mb-4">
                                        <svg className="w-8 h-8 text-[#A1AFCF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-base font-semibold text-[#00032A] mb-1">No invitations found</h3>
                                    <p className="text-sm text-gray-500 text-center max-w-xs">No invitations have been sent yet.</p>
                                </div>
                            )}
                        </FleetrifyCard>
                    </>
                )}

                {/* Manual Upload */}
                {/* {view === "manualUpload" && selectedStaff && (
                    <StaffUpload
                        onBack={() => { setView(modalAction && modalAction !== "add" ? "overview" : "invitation"); setModalAction(null); setSelectedStaff(null); }}
                        action={modalAction || "add"}
                        staff={modalAction && modalAction !== "add" ? selectedStaff : undefined}
                    />
                    <StaffProfile staff={selectedStaff} onBack={() => setView("overview")} />
                )} */}
                {/* Manual Upload */}
                {/* {view === "manualUpload" && !selectedStaff && (
                    <StaffUpload
                        onBack={() => {
                            setView(
                                modalAction && modalAction !== "add"
                                    ? "overview"
                                    : "invitation"
                            );
                            setModalAction(null);
                            setSelectedStaff(null);
                        }}
                        action={modalAction || "add"}
                        staff={
                            modalAction && modalAction !== "add"
                                ? selectedStaff || undefined
                                : undefined
                        }
                    />
                )} */}

                {/* Staff update from Staff Overview */}
                {view === "manualUpload" && modalAction === "updatestaff" && selectedStaff && (
                    <StaffProfile
                        staff={selectedStaff}
                        onBack={() => {
                            setView("overview");
                            setModalAction(null);
                            setSelectedStaff(null);
                        }}
                    />
                )}

                {/* Invitation update / add */}
                {/* {view === "manualUpload" &&
                    (modalAction === "update" || modalAction === "add") && (
                        <StaffUpload
                            onBack={() => {
                                setView(
                                    modalAction === "add"
                                        ? "invitation"
                                        : "invitation"
                                );
                                setModalAction(null);
                                setSelectedStaff(null);
                            }}
                            action={modalAction}
                            staff={selectedStaff || undefined}
                        />
                )} */}

                {view === "manualUpload" && modalAction !== "updatestaff" && (
                    <StaffUpload
                        onBack={() => {
                            if (
                                modalAction === "update" ||
                                modalAction === "resend" ||
                                modalAction === "add"
                            ) {
                                setView("invitation");
                            } else {
                                setView("overview");
                            }

                            setModalAction(null);
                            setSelectedStaff(null);
                        }}
                        action={modalAction || "add"}
                        staff={
                            modalAction && modalAction !== "add"
                                ? selectedStaff
                                : undefined
                        }
                    />
                )}

                {/* Staff Profile */}
                {view === "profile" && selectedStaff && (
                    // <StaffProfile staff={selectedStaff} onBack={() => setView("overview")} />
                    <StaffOverview staff={selectedStaff} onBack={() => setView("overview")} />
                )}
            </main>

            <StaffBulkUpload isModalOpen={showBulkUploadModal} setIsModalOpen={setShowBulkUploadModal} />

            {/* ── Unified confirmation modal ── */}
            {activeConfig && staffToAct && (
                <FleetrifyModal
                    isOpen={!!confirmType}
                    title={activeConfig.title}
                    onClose={closeConfirmModal}
                    className="max-w-md h-fit"
                    enableOutsideClick={false}
                >
                    <div className="flex flex-col items-center justify-center p-6 text-center space-y-4">
                        {/* Icon */}
                        <div className={`w-14 h-14 rounded-full border flex items-center justify-center ${activeConfig.iconBg}`}>
                            {activeConfig.icon}
                        </div>

                        {/* Copy */}
                        <div>
                            <p className="text-gray-800 font-semibold text-base">{activeConfig.title}?</p>
                            <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
                                {activeConfig.body(actingName)}
                            </p>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-3 w-full pt-1">
                            <button
                                onClick={closeConfirmModal}
                                disabled={isConfirming}
                                className="flex-1 px-4 py-2.5 text-sm font-medium bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmAction}
                                disabled={isConfirming}
                                className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${activeConfig.confirmClass}`}
                            >
                                {isConfirming ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        {activeConfig.loadingLabel}
                                    </>
                                ) : activeConfig.confirmLabel}
                            </button>
                        </div>
                    </div>
                </FleetrifyModal>
            )}
        </DashboardLayout>
    );
};

export default Staff;